import {jsonMember, jsonObject} from "typedjson";
import {DxGridColumnDataType} from "./dx-grid-column-data-type";
import {ICellRendererAngularComp} from "ag-grid-angular";


/** Опции колонки грида. */
@jsonObject
export class DxGridColumn {
  /** Идентификатор колонки (заточка под хранение опций в БД). */
  @jsonMember({name: "id", constructor: Number, emitDefaultValue: false, isRequired: false })
  private _id: number;

  /** Уникальный строковый идентификатор колонки. Ака: colA, colB */
  @jsonMember({name: "ident", constructor: String, emitDefaultValue: false, isRequired: false })
  private _ident: string;

  /** Имя колонки в хедере грида */
  @jsonMember({name: "title", constructor: String, emitDefaultValue: false, isRequired: false })
  private _title: string;

  /** Тултип, показываемый при наведении мыши на ячеку колонки в хэдере грида. */
  @jsonMember({name: "tooltip", constructor: String, emitDefaultValue: false, isRequired: false })
  private _tooltip: string;


  /**
   * Имя поля класса или геттера в объекте строки, при помощи которого будут извекаться данные
   * для данной колонки.
   */
  @jsonMember({name: "property", constructor: String, emitDefaultValue: false, isRequired: false })
  private _property: string;

  /** имя поля из dto, которое будет использоваться для серверной фильтрации и сортировки */
  @jsonMember({name: "sortFilterField", constructor: String, emitDefaultValue: false, isRequired: false })
  private _sortFilterField: string;

  /** Тип данных колонки. */
  @jsonMember({name: "dataType", constructor: DxGridColumnDataType, emitDefaultValue: false, isRequired: false })
  private _dataType: DxGridColumnDataType;

  /** Видимая \ скрытая колонка. */
  @jsonMember({name: "visible", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _visible: boolean = true;

  /** Вся колонка лочится от каких либо действий со стороны пользователя. */
  @jsonMember({name: "disabled", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _disabled: boolean = false;

  /** Тип катомного виджета, используемого для редактирования ячеек в колонке. */
  @jsonMember({name: "columnWidgetType", constructor: String, emitDefaultValue: false, isRequired: false })
  private _columnWidgetType: string;

  /** Фиксация колонки. Допустимые значения: 'left', 'right' */
  @jsonMember({name: "pinned", constructor: String, emitDefaultValue: false, isRequired: false })
  private _pinned: string;

  /** Выравнивание заголовка: 'left', 'center', 'right' */
  @jsonMember({name: "headerJustify", constructor: String, emitDefaultValue: false, isRequired: false })
  private _headerJustify: string = 'left';

  /** Включает растягивание колонки мышью. */
  @jsonMember({name: "resizable", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _resizable: boolean = true;

  /** Ширина колонки в px. */
  @jsonMember({name: "width", constructor: Number, emitDefaultValue: false, isRequired: false })
  private _width: number = 60;

  /** Максимальная ширина колонки в px. */
  @jsonMember({name: "maxWidth", constructor: Number, emitDefaultValue: false, isRequired: false })
  private _maxWidth: number;

  /** Автоматическое вычисление высоты строки в зависимости от высоты ячейки в данной колонке. */
  @jsonMember({name: "autoHeight", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _autoHeight: boolean = false;

  /** Автоматическое вычисление высоты строки в зависимости от высоты ячейки в данной колонке. */
  @jsonMember({constructor: String, emitDefaultValue: false, isRequired: false })
  public cellClass: string;

  /** Активация фильрации по колонке. */
  @jsonMember({name: "filterable", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _filterable: boolean = false;

  /** Активация сортировки по колонке. */
  @jsonMember({name: "sortable", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _sortable: boolean = false;

  /** колонку можно перемещать */
  @jsonMember({name: "movable", constructor: Boolean, emitDefaultValue: true, isRequired: false })
  private _movable: boolean = true;

  /** Дефолтное направление сортировки. */
  @jsonMember({name: "sortDirection", constructor: String, emitDefaultValue: false, isRequired: false })
  private _sortDirection: string;

  /**
   * Участвует ли колонка в дефолтной сортировке грида.
   * Этот признак является зависимым от `sortable`. То есть для колонка сначала должна быть объявлена
   * как "сортируемая", а уже потом она может быть включена в список дефолтной сортировки.
   */
  @jsonMember({name: "usedInDefaultSort", constructor: Boolean, emitDefaultValue: false, isRequired: false })
  private _usedInDefaultSort: boolean = false;

  public cellRendererFramework: ICellRendererAngularComp;


  get isPinned(): boolean {
    return !!this._pinned;
  }

  get hasTooltip(): boolean {
    return !!this._tooltip;
  }


  get id(): number {
    return this._id;
  }

  set id(value: number) {
    this._id = value;
  }

  get ident(): string {
    return this._ident;
  }

  set ident(value: string) {
    this._ident = value;
  }

  get tooltip(): string {
    return this._tooltip;
  }

  set tooltip(value: string) {
    this._tooltip = value;
  }

  get title(): string {
    return this._title;
  }

  set title(value: string) {
    this._title = value;
  }

  get property(): string {
    return this._property;
  }

  set property(value: string) {
    this._property = value;
  }

  get sortFilterField(): string {
    return this._sortFilterField;
  }

  set sortFilterField(value: string) {
    this._sortFilterField = value;
  }

  get dataType(): DxGridColumnDataType {
    return this._dataType;
  }

  set dataType(value: DxGridColumnDataType) {
    this._dataType = value;
  }

  get visible(): boolean {
    return this._visible;
  }

  set visible(value: boolean) {
    this._visible = value;
  }

  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(value: boolean) {
    this._disabled = value;
  }

  get columnWidgetType(): string {
    return this._columnWidgetType;
  }

  set columnWidgetType(value: string) {
    this._columnWidgetType = value;
  }

  get pinned(): string {
    return this._pinned;
  }

  set pinned(value: string) {
    this._pinned = value;
  }

  get resizable(): boolean {
    return this._resizable;
  }

  set resizable(value: boolean) {
    this._resizable = value;
  }

  get width(): number {
    return this._width;
  }

  set width(value: number) {
    this._width = value;
  }

  get maxWidth(): number {
    return this._maxWidth;
  }

  set maxWidth(value: number) {
    this._maxWidth = value;
  }

  get autoHeight(): boolean {
    return this._autoHeight;
  }

  set autoHeight(value: boolean) {
    this._autoHeight = value;
  }

  get filterable(): boolean {
    return this._filterable;
  }

  set filterable(value: boolean) {
    this._filterable = value;
  }

  get sortable(): boolean {
    return this._sortable;
  }

  set sortable(value: boolean) {
    this._sortable = value;
  }

  get sortDirection(): string {
    return this._sortDirection;
  }

  set sortDirection(value: string) {
    this._sortDirection = value;
  }

  get usedInDefaultSort(): boolean {
    return this._usedInDefaultSort;
  }

  set usedInDefaultSort(value: boolean) {
    this._usedInDefaultSort = value;
  }

  get movable(): boolean {
    return this._movable;
  }

  set movable(value: boolean) {
    this._movable = value;
  }

  get headerJustify(): string {
    return this._headerJustify;
  }

  set headerJustify(value: string) {
    this._headerJustify = value;
  }
}
